import React, { useState } from 'react';
import { Timer, Zap, Calculator, Trash2 } from 'lucide-react';
import { CURRENCIES } from '../constants/currencies';
import { Comparisons } from '../utils/comparisons';

export default function FixedDurationPage({ navigateTo, currency, onCurrencyChange, initialData }) {
  const [mode, setMode] = useState(initialData?.mode || 'quick');
  const [participantCount, setParticipantCount] = useState(initialData?.participantCount?.toString() || '');
  const [averageSalary, setAverageSalary] = useState(initialData?.averageSalary?.toString() || '');
  const [salaryType, setSalaryType] = useState(initialData?.salaryType || 'annual');
  const [hours, setHours] = useState(initialData?.hours?.toString() || '');
  const [minutes, setMinutes] = useState(initialData?.minutes?.toString() || '');
  const [participants, setParticipants] = useState(
    initialData?.participants || [{ id: 1, name: '', salaryType: 'annual', salaryValue: '' }]
  );

  const calculateCostPerMinute = () => {
    if (mode === 'quick') {
      if (!participantCount || !averageSalary) return 0;
      const count = parseInt(participantCount);
      const salary = parseFloat(averageSalary);
      const hourlyRate = salaryType === 'hourly' ? salary : salary / 2080;
      return (hourlyRate * count) / 60;
    } else {
      const validParticipants = participants.filter(p => p.salaryValue && parseFloat(p.salaryValue) > 0);
      if (validParticipants.length === 0) return 0;
      const totalHourlyRate = validParticipants.reduce((sum, p) => {
        const hourlyRate = p.salaryType === 'hourly' ? parseFloat(p.salaryValue) : parseFloat(p.salaryValue) / 2080;
        return sum + hourlyRate;
      }, 0);
      return totalHourlyRate / 60;
    }
  };

  const addParticipant = () => {
    if (participants.length < 20) {
      setParticipants([...participants, { id: Date.now(), name: '', salaryType: 'annual', salaryValue: '' }]);
    }
  };

  const removeParticipant = (id) => {
    if (participants.length > 1) {
      setParticipants(participants.filter(p => p.id !== id));
    }
  };

  const updateParticipant = (id, field, value) => {
    setParticipants(participants.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const calculateMeeting = () => {
    const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    
    if (totalMinutes === 0) {
      alert('Please enter meeting duration');
      return;
    }

    const costPerMinute = calculateCostPerMinute();
    
    if (costPerMinute === 0) {
      alert('Please enter valid participant information');
      return;
    }

    const totalCost = costPerMinute * totalMinutes;

    // Create participant details based on mode
    let participantDetails;
    let numParticipants;
    
    if (mode === 'quick') {
      const count = parseInt(participantCount);
      numParticipants = count;
      participantDetails = [];
      for (let i = 0; i < count; i++) {
        participantDetails.push({
          id: Date.now() + i,
          name: `Participant ${i + 1}`,
          salaryType: salaryType,
          salaryValue: averageSalary.toString(),
          isQuickMode: false,
          isFixedDuration: true
        });
      }
    } else {
      participantDetails = participants.filter(p => p.salaryValue && parseFloat(p.salaryValue) > 0);
      participantDetails = participantDetails.map(p => ({ ...p, isFixedDuration: true }));
      numParticipants = participantDetails.length;
    }

    const meetingData = {
      duration: Math.round(totalMinutes),
      durationSeconds: totalMinutes * 60,
      participants: numParticipants,
      participantDetails: participantDetails,
      totalCost: totalCost,
      costPerMinute: costPerMinute,
      comparison: Comparisons.getComparison(totalCost),
      visualComparisons: Comparisons.getVisualComparisons(totalCost),
      currency: currency
    };

    navigateTo('results', meetingData);
  };

  const costPerMinute = calculateCostPerMinute();
  const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
  const estimatedCost = costPerMinute * totalMinutes;

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-teal-600 rounded-lg flex items-center justify-center">
            <Timer className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Fixed Duration Calculator</h1>
            <p className="text-gray-600">Calculate cost by entering exact meeting duration</p>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Calculation Mode</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMode('quick')}
              className={`px-4 py-3 rounded-lg font-medium border-2 transition-all flex items-center justify-center gap-2 ${
                mode === 'quick'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              <Zap className="w-4 h-4" />
              Quick Mode
            </button>
            <button
              onClick={() => setMode('custom')}
              className={`px-4 py-3 rounded-lg font-medium border-2 transition-all flex items-center justify-center gap-2 ${
                mode === 'custom'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
              }`}
            >
              <Calculator className="w-4 h-4" />
              Custom Mode
            </button>
          </div>
        </div>

        {/* Currency Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
          <select
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value)}
            className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg shadow-sm text-base font-medium focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            {Object.entries(CURRENCIES).map(([code, info]) => (
              <option key={code} value={code}>
                {info.symbol} {code} - {info.name}
              </option>
            ))}
          </select>
        </div>

        {/* Quick Mode Inputs */}
        {mode === 'quick' && (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Participants</label>
              <input
                type="number"
                value={participantCount}
                onChange={(e) => setParticipantCount(e.target.value)}
                placeholder="e.g., 5"
                min="1"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Average Salary Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSalaryType('annual')}
                  className={`px-4 py-3 rounded-lg font-medium border-2 transition-all ${
                    salaryType === 'annual'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-300'
                  }`}
                >
                  Annual Salary
                </button>
                <button
                  onClick={() => setSalaryType('hourly')}
                  className={`px-4 py-3 rounded-lg font-medium border-2 transition-all ${
                    salaryType === 'hourly'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-300'
                  }`}
                >
                  Hourly Rate
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Average {salaryType === 'annual' ? 'Annual Salary' : 'Hourly Rate'}
              </label>
              <input
                type="number"
                value={averageSalary}
                onChange={(e) => setAverageSalary(e.target.value)}
                placeholder={salaryType === 'annual' ? 'e.g., 80000' : 'e.g., 45'}
                min="0"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>
          </>
        )}

        {/* Custom Mode Inputs */}
        {mode === 'custom' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Participants</label>
            <div className="participant-scroll space-y-4 max-h-96 pr-2 border border-gray-200 rounded-lg p-4 bg-gray-50" style={{ overflowY: 'scroll' }}>
              {participants.map((participant, index) => (
                <div key={participant.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-semibold">Participant {index + 1}</span>
                    {participants.length > 1 && (
                      <button
                        onClick={() => removeParticipant(participant.id)}
                        className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={participant.name}
                      onChange={(e) => updateParticipant(participant.id, 'name', e.target.value)}
                      placeholder="Name (optional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => updateParticipant(participant.id, 'salaryType', 'annual')}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          participant.salaryType === 'annual'
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-gray-300'
                        }`}
                      >
                        Annual
                      </button>
                      <button
                        onClick={() => updateParticipant(participant.id, 'salaryType', 'hourly')}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          participant.salaryType === 'hourly'
                            ? 'bg-green-600 text-white'
                            : 'bg-white border border-gray-300'
                        }`}
                      >
                        Hourly
                      </button>
                    </div>
                    <input
                      type="number"
                      value={participant.salaryValue}
                      onChange={(e) => updateParticipant(participant.id, 'salaryValue', e.target.value)}
                      placeholder={participant.salaryType === 'annual' ? 'Annual salary' : 'Hourly rate'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={addParticipant}
              disabled={participants.length >= 20}
              className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:bg-gray-400 font-medium"
            >
              + Add Participant
            </button>
          </div>
        )}

        {/* Meeting Duration */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Duration</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Hours</label>
              <input
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="0"
                min="0"
                max="23"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Minutes</label>
              <input
                type="number"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="0"
                min="0"
                max="59"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>
          </div>
        </div>

        {/* Estimated Cost Preview - COMMENTED OUT */}
        {/* {estimatedCost > 0 && (
          <div className="mb-6 p-6 bg-green-50 rounded-lg border-2 border-green-200">
            <p className="text-sm text-gray-600 mb-1">Estimated Total Cost:</p>
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(estimatedCost, currency)}
            </p>
            {totalMinutes > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                {totalMinutes} minute{totalMinutes !== 1 ? 's' : ''} × {formatCurrency(costPerMinute, currency)}/min
              </p>
            )}
          </div>
        )} */}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={() => navigateTo('home')}
            className="px-6 py-3 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 font-semibold"
          >
            ← Back
          </button>
          <button
            onClick={calculateMeeting}
            disabled={estimatedCost === 0}
            className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500 transition-all"
          >
            Calculate Results
          </button>
        </div>
      </div>
    </div>
  );
}